'use strict';

define('vb/private/vx/endpointExtensionAdapter',[
  'vb/private/vx/baseExtensionAdapter',
  'vbc/private/utils',
  'vb/private/constants',
], (
  BaseExtensionAdapter,
  Utils,
  Constants,
) => {
  /**
   * The extension adapter used to load extension from the extension manager using an endpoint URL
   */
  class EndpointExtensionAdapter extends BaseExtensionAdapter {
    /**
     * Load the specified types from the remote extension digest
     * @param {String[]} types
     * @param {Object} [options]
     * @param {Boolean} [options.rejectOnError=false] whether a digest fetch failure should reject
     * @returns {Array<Promise<Array<Object>>>}
     */
    loadDigests(types = [], { rejectOnError = false } = {}) {
      const url = this.getUrl();

      const promises = types.map((type) => {
        url.search = `type=${type}`;

        return this.getFetchManifestPromise(url.href, `Extension Manager digest ${type}`)
          .then((results) => {
            const digest = (results && results.digest) || {};

            if (type === Constants.Digest.RUNTIME) {
              return EndpointExtensionAdapter.readRuntimeDigest(digest[type]);
            }
            if (type === Constants.Digest.REQUIREJS_INFO) {
              return EndpointExtensionAdapter.readRequirejsInfoDigest(digest[type]);
            }
            if (type === Constants.Digest.PWA_INFO) {
              return EndpointExtensionAdapter.readPWAInfoDigest(digest[type]);
            }

            return digest[type] || [];
          })
          .catch((error) => {
            this.log.error(`Error while fetching extension manager digest '${type}'.`, error);
            if (rejectOnError) {
              throw error;
            }
            // Ordinary startup remains best effort: a failed optional digest is treated as empty.
            return [];
          });
      });

      return promises;
    }

    /**
     * Load the partial digest URL supplied by the bootstrap configuration. The runtime does not infer the
     * endpoint shape: RegistryConfig derives this URL from the initial DIGEST_PROMISE request.
     * @param {String} partialDigestUrl URL for the target App UI partial digest
     * @returns {Promise<Array<Promise<Array<Object>>>>}
     */
    loadPartialDigest(partialDigestUrl) {
      return this.getFetchManifestPromise(partialDigestUrl, 'Extension Manager partial digest')
        .then((results) => {
          const digest = (results && results.digest) || {};
          return [
            Promise.resolve(EndpointExtensionAdapter.readRuntimeDigest(digest[Constants.Digest.RUNTIME])),
            Promise.resolve(EndpointExtensionAdapter.readRequirejsInfoDigest(digest[Constants.Digest.REQUIREJS_INFO])),
            Promise.resolve(digest[Constants.Digest.APP_UI_INFO] || []),
          ];
        });
    }

    /**
     * Builds paths from a vb-bundle format
     *
     * @param {string} extId
     * @param {Array<String>}  bundle
     * @param {Array<String>}  bundledResources
     * @param {Object}  folders
     * @param {string} [currentPath='']
     * @private
     */
    static buildPaths(extId, bundle, bundledResources, folders, currentPath = '') {
      if (!folders || typeof folders !== 'object') {
        return;
      }

      const folderFiles = folders.files;
      const hasFiles = Array.isArray(folderFiles);
      let i;

      // If "files" was a folder name, its type would not be an array
      if (hasFiles) {
        for (i = 0; i < folderFiles.length; i += 1) {
          const bundleFile = folderFiles[i];
          const exclIndex = bundleFile.indexOf('!');
          const plugin = exclIndex > 0 ? bundleFile.slice(0, exclIndex + 1) : '';
          const file = exclIndex > 0 ? bundleFile.slice(exclIndex + 1) : bundleFile;
          const bundledPath = `${currentPath}${file}`;
          const lastDotIndex = bundledPath.lastIndexOf('.');
          let path = bundledPath;

          if (plugin !== 'text!') {
            path = lastDotIndex > 0 ? bundledPath.slice(0, lastDotIndex) : '';
          }

          bundledResources.push(bundledPath);
          bundle.push(`${plugin}${extId}${path}`);
        }
      }

      Object.keys(folders).forEach((name) => {
        if (!hasFiles || name !== 'files') {
          EndpointExtensionAdapter.buildPaths(extId, bundle, bundledResources, folders[name], `${currentPath}${name}/`);
        }
      });
    }

    /**
     * Convert a requirejs-info new vb-bundles format to an object of the old format
     */
    static convertVbBundles(vbBundles) {
      const bundledResources = [];
      const bundles = {};

      vbBundles.forEach((bundle) => {
        if (!bundles[bundle.bundleId]) {
          bundles[bundle.bundleId] = [];
        }
        const extId = `vx/${bundle.bundleId.split('/')[1]}/`;
        EndpointExtensionAdapter.buildPaths(extId, bundles[bundle.bundleId], bundledResources, bundle.files);
      });

      return { bundledResources, bundles };
    }

    /**
     * For each extension in the runtime digest, convert the vb-files object
     * to an array of files with path
     * @param {Array<Object>} runtime
     * @returns {Array<Object>} the converted runtime digest
     */
    static readRuntimeDigest(runtime = []) {
      runtime.forEach((extension) => {
        const vbFiles = extension[Constants.Runtime.VB_FILES];

        if (vbFiles) {
          /* eslint-disable no-param-reassign */
          extension.files = BaseExtensionAdapter.convertVbFiles(vbFiles);

          delete extension[Constants.Runtime.VB_FILES];
        }
      });

      return runtime;
    }

    /**
     * Read the requirejs-info using the old or the new shrunk format
     * @param {Array<Object>} requirejsInfo
     * @returns {Array<Object>} the converted requirejsInfo
     */
    static readRequirejsInfoDigest(requirejsInfo = []) {
      requirejsInfo.forEach((extension) => {
        const configurations = extension.metadata && extension.metadata.configurations;
        if (configurations) {
          const { build } = configurations;

          const vbBundles = build && build[Constants.RequirejsInfo.VB_BUNDLES];
          if (vbBundles) {
            const { bundledResources, bundles } = EndpointExtensionAdapter.convertVbBundles(vbBundles);

            build.bundledResources = bundledResources;
            build.bundles = bundles;

            delete build[Constants.RequirejsInfo.VB_BUNDLES];
          }
        }
      });

      return requirejsInfo;
    }

    /**
     * Read the pwa-info and interpret it.
     */
    static readPWAInfoDigest(pwaInfo = []) {
      return pwaInfo.map((extension) => {
        // Collapse the extra metadata level
        const metadata = extension.metadata && extension.metadata.metadata;
        if (metadata) {
          return extension.metadata;
        }
        return extension;
      });
    }

    /**
     * Return a URL object initialized with the registry URL string
     *
     * @return {URL}
     */
    getUrl() {
      const url = super.getUrl();

      const version = this.getExtensionVersion();
      // The version string has already been encoded, so don't encoded it again in
      // order to preserve the $ character
      url.pathname = `${Utils.addTrailingSlash(url.pathname)}${version || ''}`;

      return url;
    }
  }

  return EndpointExtensionAdapter;
});

